/**
 * by_kind breakdown — replaces category-rank. Each kind with count + avg warmth.
 */
import { store } from '../store.ts';
import { RELATIONSHIP_DISPLAY, RELATIONSHIP_KINDS } from '../types.ts';
import type { Person, RelationshipKind } from '../types.ts';

export class DialRankByKind extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.inner_circle, s.summary.by_kind);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(circle: Person[], by_kind: Partial<Record<RelationshipKind, number>>): void {
    const avgByKind = new Map<RelationshipKind, number>();
    for (const k of RELATIONSHIP_KINDS) {
      const subset = circle.filter((p) => p.relationship_kind === k);
      if (!subset.length) continue;
      const avg = subset.reduce((s, p) => s + p.warmth_score, 0) / subset.length;
      avgByKind.set(k, avg);
    }
    const entries: Array<[RelationshipKind, number, number]> = [];
    for (const k of RELATIONSHIP_KINDS) {
      const cnt = by_kind[k] ?? 0;
      if (cnt > 0) entries.push([k, cnt, avgByKind.get(k) ?? 0]);
    }
    entries.sort((a, b) => b[2] - a[2]); // by avg warmth descending
    if (!entries.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const maxCount = Math.max(...entries.map((e) => e[1]));
    const rows = entries.map(([k, c, avg]) => /* html */ `
<div class="row" style="--pct:${(c / maxCount) * 100}%">
  <div class="kind">
    <div class="name">${RELATIONSHIP_DISPLAY[k]}</div>
    <div class="meter"><span></span></div>
  </div>
  <div class="count">${c}</div>
  <div class="avg">avg ${Math.round(avg)}</div>
</div>`).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  view-transition-name: rank-by-kind;
}
.title { font-family: var(--mono); font-size: 10px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 14px; padding-left: 4px; }
.list { display: grid; gap: 2px; background: var(--paper-up); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  transition: background 220ms cubic-bezier(0.32, 0.72, 0, 1);
}
.row:hover {
  background: color-mix(in oklch, var(--peach) 12%, var(--paper-up));
}
.row:last-child { border-bottom: none; }
.kind { display: grid; gap: 6px; min-width: 0; }
.name { font-family: var(--display); font-size: 19px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; }
.meter { height: 3px; background: oklch(94% 0.03 25); border-radius: 999px; overflow: hidden; }
.meter span { display: block; height: 100%; width: var(--pct); background: linear-gradient(90deg, var(--peach-deep), var(--peach)); animation: g 700ms var(--tx-snap) both; }
.count {
  font-family: var(--display);
  font-size: 26px;
  font-weight: 700;
  color: var(--maroon);
  font-feature-settings: 'tnum' 1, 'ss01' 1, 'cv11' 1, 'lnum' 1;
  letter-spacing: -0.022em;
  font-style: italic;
  line-height: 1;
}
.avg {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-faint);
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-feature-settings: 'tnum' 1;
}
@keyframes g { from { transform: scaleX(0); transform-origin: left; } to { transform: scaleX(1); } }
</style>
<div class="title">By relationship</div>
<div class="list">${rows}</div>
`;
  }
}

customElements.define('dial-rank-by-kind', DialRankByKind);
